/**
 * Negozi e-commerce dell'org con stato dal vivo (08 sez. 3.2, principio 10).
 * Una sola lettura e un solo canale realtime su commerce_stores per org, condivisi da tutti i componenti
 * montati (conteggio dei consumatori: il canale si chiude con l'ultimo). Il realtime rispetta la RLS:
 * arriva solo a chi ha settings_ecommerce.view. sync_state si aggiorna al massimo ogni 3 s per negozio.
 * Modello: useDuplicateScanJob.ts.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from '../stores/referenceDataCache';
import { fetchStores } from '../commerce/queries';
import { applyStoreChange, sortStores, type RealtimeChange } from '../commerce/storeList';
import type { CommerceStoreRow } from '../commerce/types';

interface Snapshot {
  stores: CommerceStoreRow[];
  loading: boolean;
  error: string | null;
}

type Listener = (s: Snapshot) => void;

const source = {
  orgId: null as string | null,
  snapshot: { stores: [], loading: true, error: null } as Snapshot,
  listeners: new Set<Listener>(),
  channel: null as ReturnType<typeof supabase.channel> | null,
  inFlight: null as Promise<void> | null,
};

function publish(next: Partial<Snapshot>) {
  source.snapshot = { ...source.snapshot, ...next };
  for (const l of source.listeners) l(source.snapshot);
}

async function load(): Promise<void> {
  if (source.inFlight) return source.inFlight;
  source.inFlight = (async () => {
    const r = await fetchStores(true);
    if (r.error !== null) publish({ loading: false, error: r.error });
    else publish({ loading: false, error: null, stores: sortStores(r.data) });
  })().finally(() => {
    source.inFlight = null;
  });
  return source.inFlight;
}

function subscribe(orgId: string) {
  if (source.channel) return;
  source.channel = supabase
    .channel(`commerce-stores-${orgId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'commerce_stores', filter: `organization_id=eq.${orgId}` },
      (payload) => {
        const change = payload as unknown as RealtimeChange<CommerceStoreRow>;
        publish({ stores: sortStores(applyStoreChange(source.snapshot.stores, change)) });
      },
    )
    // Ad ogni (ri)aggancio del canale si rilegge: le modifiche arrivate mentre il canale era chiuso
    // (ultimo componente smontato, rete caduta) non passano dal realtime. load() unisce le richieste in volo.
    .subscribe((status) => {
      if (status === 'SUBSCRIBED' && !source.snapshot.loading) void load();
    });
}

function teardown() {
  if (source.channel) void supabase.removeChannel(source.channel);
  source.channel = null;
}

async function attach(listener: Listener): Promise<void> {
  source.listeners.add(listener);
  const orgId = await getCurrentUserOrganizationId().catch(() => null);
  if (!source.listeners.has(listener)) return;
  if (!orgId) {
    publish({ loading: false, error: 'organizzazione non trovata' });
    return;
  }
  if (source.orgId !== orgId) {
    // cambio di org (impersonate, cambio account): si riparte da zero
    teardown();
    source.orgId = orgId;
    source.snapshot = { stores: [], loading: true, error: null };
  }
  subscribe(orgId);
  if (source.snapshot.loading || source.snapshot.error) await load();
  else listener(source.snapshot);
}

function detach(listener: Listener) {
  source.listeners.delete(listener);
  if (source.listeners.size === 0) teardown();
}

/** Ricarica esplicita (dopo un'azione dell'utente, o "Riprova" su un errore di lettura). */
export async function reloadCommerceStores(): Promise<void> {
  publish({ loading: source.snapshot.stores.length === 0 });
  await load();
}

export interface UseCommerceStores extends Snapshot {
  reload: () => Promise<void>;
}

/** `enabled = false`: nessuna lettura e nessun canale (org senza negozi o utente senza permesso). */
export function useCommerceStores(enabled = true): UseCommerceStores {
  const [snap, setSnap] = useState<Snapshot>(enabled ? source.snapshot : { stores: [], loading: false, error: null });

  useEffect(() => {
    if (!enabled) return;
    const listener: Listener = (s) => setSnap(s);
    void attach(listener);
    return () => detach(listener);
  }, [enabled]);

  const reload = useCallback(() => reloadCommerceStores(), []);
  return { ...snap, reload };
}

/** Un negozio solo, dallo stesso canale condiviso. */
export function useCommerceStore(storeId: string | null | undefined, enabled = true) {
  const all = useCommerceStores(enabled && !!storeId);
  const store = storeId ? all.stores.find((s) => s.id === storeId) ?? null : null;
  return { store, loading: all.loading, error: all.error, reload: all.reload };
}
